"use client";

import FinalCTAClient from "./FinalCTAClient";
import { FinalCTAData } from "@/types/data";
import { useEffect, useRef, useId, useState } from "react";
import { gsap } from "@/lib/gsap";

export default function FinalCTA({ data }: { data: FinalCTAData["content"] }) {
  const { tagline, title, description, primaryButton, secondaryButton } = data;
  const gradientId = useId();
  const [isMounted, setIsMounted] = useState(false);

  const sectionRef = useRef<HTMLElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const ctaButtonsRef = useRef<HTMLDivElement>(null);

  // Set mounted state after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!sectionRef.current || !isMounted) return;

    const runAnimation = () => {
      const elementsToAnimate = [
        taglineRef.current,
        titleRef.current,
        descriptionRef.current,
        ctaButtonsRef.current,
      ].filter(Boolean);

      const timeline = gsap.timeline({
        onComplete: () => {
          // Clean up after animation
          if (elementsToAnimate.length > 0) {
            gsap.set(elementsToAnimate, {
              willChange: "auto",
            });
          }
        },
      });

      if (taglineRef.current) {
        timeline.fromTo(
          taglineRef.current,
          { opacity: 0, y: 30, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          }
        );
      }

      if (titleRef.current) {
        timeline.fromTo(
          titleRef.current,
          {
            opacity: 0,
            y: 50,
            scale: 0.95,
            filter: "blur(8px)",
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.3"
        );
      }

      if (descriptionRef.current) {
        timeline.fromTo(
          descriptionRef.current,
          { opacity: 0, y: 30, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          "-=0.5"
        );
      }

      if (ctaButtonsRef.current) {
        timeline.fromTo(
          ctaButtonsRef.current,
          { opacity: 0, y: 20, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            scale: 1,
            duration: 0.7,
            ease: "power2.out",
          },
          "-=0.4"
        );
      }
    };

    let observer: IntersectionObserver | null = null;
    let hasAnimated = false;

    // Set initial hidden state after hydration to prevent CLS
    const elementsToAnimate = [
      taglineRef.current,
      titleRef.current,
      descriptionRef.current,
      ctaButtonsRef.current,
    ].filter(Boolean);

    if (elementsToAnimate.length > 0) {
      gsap.set(elementsToAnimate, {
        opacity: 0,
        y: 30,
        filter: "blur(8px)",
      });
    }

    // Use double requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!sectionRef.current) return;

        const rect = sectionRef.current.getBoundingClientRect();
        const isVisible =
          typeof window !== "undefined" &&
          rect.top < window.innerHeight &&
          rect.bottom > 0;

        if (isVisible && !hasAnimated) {
          hasAnimated = true;
          runAnimation();
        } else if (!isVisible) {
          const elementsToAnimate = [
            taglineRef.current,
            titleRef.current,
            descriptionRef.current,
            ctaButtonsRef.current,
          ].filter(Boolean);

          if (elementsToAnimate.length > 0) {
            gsap.set(elementsToAnimate, {
              opacity: 0,
              y: 30,
              filter: "blur(8px)",
            });
          }

          observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && !hasAnimated) {
                  hasAnimated = true;
                  runAnimation();
                  observer?.disconnect();
                }
              });
            },
            {
              threshold: 0.3,
              rootMargin: "0px 0px -50px 0px",
            }
          );

          if (sectionRef.current && observer) {
            observer.observe(sectionRef.current);
          }
        }
      });
    });

    // Cleanup
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [isMounted]);

  return (
    <section
      ref={sectionRef}
      className="section-padding bg-theme-secondary min-h-[480px]"
    >
      <div className="container">
        <div className="cta-section relative isolate overflow-hidden bg-[#5A7DB6] px-6 py-16 text-center shadow-2xl rounded-2xl sm:px-16 min-h-[480px] flex flex-col justify-center items-center">
          {tagline && (
            <div
              ref={taglineRef}
              className="flex items-center justify-center mb-4 will-change-transform opacity-0 min-h-[1.5rem]"
              style={{
                transform: "translateY(30px)",
                filter: "blur(8px)",
              }}
            >
              <div className="w-5 h-0.5 bg-white mr-3"></div>
              <p className="text-xs font-semibold text-white tracking-wider uppercase">
                {tagline}
              </p>
            </div>
          )}
          {title && (
            <h2
              ref={titleRef}
              className="font-lora mx-auto max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl xl:text-6xl will-change-transform opacity-0 min-h-[3rem] sm:min-h-[4rem] xl:min-h-[5rem]"
              style={{
                transform: "translateY(50px) scale(0.95)",
                filter: "blur(8px)",
              }}
            >
              {title}
            </h2>
          )}
          {description && (
            <p
              ref={descriptionRef}
              className="mx-auto mt-4 max-w-xl text-lg leading-8 text-[#D1D5DB] will-change-transform opacity-0 min-h-[4rem]"
              style={{
                transform: "translateY(30px)",
                filter: "blur(8px)",
              }}
            >
              {description}
            </p>
          )}
          <div ref={ctaButtonsRef} className="mt-8 opacity-0 min-h-[3rem]">
            <FinalCTAClient {...{ primaryButton, secondaryButton }} />
          </div>
          <svg
            viewBox="0 0 1024 1024"
            className="absolute left-1/2 top-1/2 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
            aria-hidden="true"
            suppressHydrationWarning
          >
            <circle
              cx="512"
              cy="512"
              r="512"
              fill={`url(#${gradientId})`}
              fillOpacity="0.7"
            />
            <defs>
              <radialGradient id={gradientId}>
                <stop stopColor="#F97316" />
                <stop offset="1" stopColor="#EA580C" />
              </radialGradient>
            </defs>
          </svg>
        </div>
      </div>
    </section>
  );
}
